import { Link } from "react-router-dom";
import { Instagram, Phone, MapPin, Car, MapPinned, Wrench, Mail, FileText, ExternalLink } from "lucide-react";
import { BRAND, PHONES, buildTelLink } from "@/lib/constants";

const services = [
    { label: "Aluguel de Veículos", to: "/aluguel-de-veiculos", phone: PHONES.aluguel, Icon: Car },
    { label: "Rastreamento Veicular", to: "/rastreamento-veicular", phone: PHONES.rastreamento, Icon: MapPinned },
    { label: "Guincho 24h", to: "/guincho-24-horas", phone: PHONES.guincho, Icon: Wrench },
];

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="bg-[#1a1a1a] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    {/* Brand */}
                    <div className="md:col-span-5">
                        <img
                            src={BRAND.logoDark}
                            alt="PEG Serviços"
                            className="h-20 w-auto object-contain"
                        />
                        <p className="font-display text-2xl font-bold mt-6 leading-tight">
                            {BRAND.tagline}
                        </p>
                        <p className="text-white/60 mt-2 max-w-md">
                            {BRAND.sub}
                        </p>

                        <div className="mt-6 space-y-2 text-sm text-white/70">
                            <a
                                href={BRAND.address.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-address"
                                className="flex items-start gap-2 hover:text-[#fec200] transition-colors"
                            >
                                <MapPin className="w-4 h-4 text-[#fec200] flex-shrink-0 mt-0.5" />
                                <span>
                                    {BRAND.address.line}<br />
                                    {BRAND.address.city} — {BRAND.address.zip}
                                </span>
                            </a>
                            <a
                                href={`mailto:${BRAND.email}`}
                                data-testid="footer-email"
                                className="flex items-center gap-2 hover:text-[#fec200] transition-colors"
                            >
                                <Mail className="w-4 h-4 text-[#fec200] flex-shrink-0" />
                                <span>{BRAND.email}</span>
                            </a>
                            <div className="flex items-center gap-2" data-testid="footer-cnpj">
                                <FileText className="w-4 h-4 text-[#fec200] flex-shrink-0" />
                                <span>CNPJ {BRAND.cnpj}</span>
                            </div>
                        </div>

                        {/* Mini map */}
                        <a
                            href={BRAND.address.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-map-link"
                            className="block relative mt-5 group overflow-hidden border-2 border-[#fec200]"
                        >
                            <iframe
                                title="Localização PEG Serviços"
                                src={BRAND.address.embedUrl}
                                width="100%"
                                height="180"
                                style={{ border: 0, pointerEvents: "none" }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="footer-map-iframe"
                            />
                            <div className="absolute inset-0 bg-[#1a1a1a]/30 group-hover:bg-[#1a1a1a]/10 transition-colors flex items-end justify-end p-3">
                                <span className="inline-flex items-center gap-1.5 bg-[#fec200] text-[#1a1a1a] text-xs font-bold px-3 py-1.5">
                                    <ExternalLink className="w-3 h-3" />
                                    Abrir no Maps
                                </span>
                            </div>
                        </a>

                        <a
                            href={BRAND.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-instagram"
                            className="mt-6 inline-flex items-center gap-2 text-white hover:text-[#fec200] transition-colors"
                        >
                            <Instagram className="w-5 h-5" />
                            <span className="font-semibold">{BRAND.instagramHandle}</span>
                        </a>
                    </div>

                    {/* Services list */}
                    <div className="md:col-span-4">
                        <h4 className="text-sm uppercase tracking-widest text-[#fec200] font-bold mb-5">
                            Nossos Serviços
                        </h4>
                        <ul className="space-y-4">
                            {services.map(({ label, to, phone, Icon }) => (
                                <li
                                    key={to}
                                    data-testid={`footer-service-${to.replace(/\//g, "")}`}
                                    className="flex items-start gap-3"
                                >
                                    <Icon className="w-5 h-5 text-[#fec200] flex-shrink-0 mt-0.5" />
                                    <div>
                                        <Link
                                            to={to}
                                            className="font-semibold hover:text-[#fec200] transition-colors"
                                        >
                                            {label}
                                        </Link>
                                        <a
                                            href={buildTelLink(phone.raw)}
                                            className="block text-sm text-white/60 hover:text-white items-center gap-1 mt-0.5"
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                {phone.display}
                                            </span>
                                        </a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Quick links */}
                    <div className="md:col-span-3">
                        <h4 className="text-sm uppercase tracking-widest text-[#fec200] font-bold mb-5">
                            Navegação
                        </h4>
                        <ul className="space-y-3 text-white/70">
                            <li><Link to="/" className="hover:text-white">Início</Link></li>
                            <li><Link to="/sobre" className="hover:text-white">Sobre a PEG</Link></li>
                            <li><Link to="/aluguel-de-veiculos" className="hover:text-white">Aluguel de Veículos</Link></li>
                            <li><Link to="/rastreamento-veicular" className="hover:text-white">Rastreamento</Link></li>
                            <li><Link to="/guincho-24-horas" className="hover:text-white">Guincho 24h</Link></li>
                            <li><Link to="/contato" className="hover:text-white">Contato</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p className="text-sm text-white/50">
                        © {new Date().getFullYear()} {BRAND.name}. Todos os direitos reservados.
                    </p>
                    <p className="text-xs text-white/40 uppercase tracking-widest">
                        Onde a clareza guia e a atitude transforma.
                    </p>
                </div>
            </div>
        </footer>
    );
}
